import { Grid, Utils } from 'common';
import drop from 'lodash/drop';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import take from 'lodash/take';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import EmptyContactsGridView from './EmptyContactsGridView.component.js';
import Contact from './utils/Contact';

const { GridPanel } = Grid;
const { Session, Validation } = Utils;

const columnDefs = [
	{
		colId: 'FullName',
		headerName: `Name`,
		field: 'FullName',
		headerCheckboxSelection: true,
		checkboxSelection: true,
		minWidth: 200,
	},
	{
		colId: 'Prefix',
		headerName: `Prefix`,
		field: 'Prefix',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'FirstName',
		headerName: `First Name`,
		field: 'FirstName',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'MiddleName',
		headerName: `Middle Name`,
		field: 'MiddleName',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'LastName',
		headerName: `Last Name`,
		field: 'LastName',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'Suffix',
		headerName: `Suffix`,
		field: 'Suffix',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'Company',
		headerName: `Company`,
		field: 'Company',
		minWidth: 200,
	},
	{
		colId: 'Title',
		headerName: `Title`,
		field: 'Title',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'Department',
		headerName: `Department`,
		field: 'Department',
		minWidth: 200,
		hide: true,
	},
	{
		colId: 'Email',
		headerName: `Email`,
		field: 'Email',
		minWidth: 150,
		cellRenderer: params => {
			const email = params.value;
			if (typeof email !== 'string' || !Validation.isValidEmail(email)) {
				return '';
			}
			// Build DOM nodes so the contact email is never parsed as markup.
			const anchor = document.createElement('a');
			anchor.href = `mailto:${encodeURIComponent(email).replace(/%40/g, '@')}`;
			anchor.textContent = email;
			return anchor;
		},
	},
	{
		colId: 'Phone',
		headerName: `Phone`,
		field: 'Phone',
		minWidth: 130,
	},
	{
		colId: 'PhoneExt',
		headerName: `Ext.`,
		field: 'PhoneExt',
		minWidth: 125,
		hide: true,
	},
	{
		colId: 'Street1',
		headerName: `Street Address`,
		field: 'Street1',
		minWidth: 200,
	},
	{
		colId: 'City',
		headerName: `City`,
		field: 'City',
		minWidth: 130,
	},
	{
		colId: 'State',
		headerName: `State/Prv`,
		field: 'State',
		minWidth: 80,
		valueGetter: ({ data: { CountryCode, Province, State } }) => {
			if (CountryCode !== 'US') {
				return Province;
			}

			return State;
		},
	},
	{
		colId: 'PostalCode',
		headerName: `Postal Code`,
		field: 'PostalCode',
		minWidth: 130,
		valueGetter: ({ data: { PostalCode, Zip, ZipAddOn } }) => {
			const zipCode =
				Zip && Zip.trim() !== '' && ZipAddOn && ZipAddOn.trim() !== ''
					? `${Zip}-${ZipAddOn}`
					: Zip;
			return PostalCode && PostalCode.trim() !== '' ? PostalCode : zipCode;
		},
	},
	{
		colId: 'CountryCode',
		headerName: `Country`,
		field: 'CountryCode',
		minWidth: 130,
	},
	{
		colId: 'CostCode',
		headerName: `Cost Code`,
		field: 'CostCode',
		minWidth: 130,
	},
	{
		colId: 'ReferenceNumber',
		headerName: `Reference #`,
		field: 'ReferenceNumber',
		minWidth: 130,
	},
];

const ContactsGrid = ({
	isModal,
	mode,
	contacts,
	pageNumber,
	pageSize,
	totalCount,
	onGridReady,
	localStorageKey,
	activeMenuItemId,
	fetchContacts,
	setSelectedContacts,
	setContactsFromLocalStorage,
}) => {
	const { statefulViews } = Session.getPreferences();
	const statefulColumnViews =
		statefulViews && statefulViews !== ''
			? JSON.parse(decodeURIComponent(statefulViews))
			: {};
	const columnState = isEmpty(statefulColumnViews.ContactsGridAgGrid)
		? []
		: statefulColumnViews.ContactsGridAgGrid;
	const totalPages = Math.ceil(totalCount / pageSize) || 1;
	const rowData = contacts ? contacts.map(contact => Contact(contact)) : null;

	// on mount we remove any previous instances of selected contacts being saved to local storage
	useEffect(() => {
		localStorage.removeItem(localStorageKey);
	}, []);

	const buildSortedBy = ({ colId = '' }) => {
		if (colId === 'FullName') {
			return 'FirstName';
		} else {
			return colId;
		}
	};

	const onSortChanged = sortModel => {
		const sortColumn = sortModel[0] ? sortModel[0] : {};

		fetchContacts({
			SortedBy: buildSortedBy(sortColumn),
			SortedDesc: sortColumn.sort === 'desc',
		});
	};

	const fetchContactsByPage = (nextPage, nextPageSize) => {
		fetchContacts({
			PageSize: nextPageSize,
			PageNumber: nextPage,
		});
	};

	const onSelectionChanged = selection => {
		const selectedContacts = selection
			.map(({ data }) => data)
			.filter(({ ContactId }) => ContactId);

		if (!isModal) {
			// store selected contact data into localStorage for Print Postage use
			localStorage.setItem(localStorageKey, JSON.stringify(selectedContacts));
		}

		setSelectedContacts(selectedContacts);
	};

	/**
	 * Preserve the column changes in users' preference
	 * @param {object} newColumnState
	 */
	const onColumnStateChanged = newColumnState => {
		const newStatefulColumnViews = {
			...statefulColumnViews,
			ContactsGridAgGrid: newColumnState,
		};

		Session.savePreferences([
			{
				Name: 'statefulViews',
				Value: encodeURIComponent(JSON.stringify(newStatefulColumnViews)),
			},
		]);
	};

	// retrieves contacts from local storage when changing pages
	const fetchContactsByPageFromLocalStorage = (
		currentPage,
		newPageSize,
		sortModel
	) => {
		const contactsRowData = contacts
			? contacts.map(contact => Contact(contact))
			: null;
		const sortedRowData =
			sortModel && sortModel.length === 1
				? orderBy(contactsRowData, [sortModel[0].colId], [sortModel[0].sort])
				: contactsRowData;
		const rowDataToDisplay = take(
			drop(
				sortedRowData,
				currentPage >= 1 ? (currentPage - 1) * newPageSize : 0
			),
			newPageSize
		);

		setContactsFromLocalStorage({
			rowData: rowDataToDisplay,
			totalCount: contactsRowData.length,
			newPageSize,
		});
	};

	// sorts contacts when using local storage data
	const sortContactsFromLocalStorage = sortModel => {
		fetchContactsByPageFromLocalStorage(pageNumber, pageSize, sortModel);
	};

	return (
		<div className="contacts-grid">
			<GridPanel
				rowData={rowData}
				pageSize={pageSize}
				columnDefs={columnDefs}
				columnState={columnState}
				totalPages={totalPages}
				currentPage={pageNumber}
				onGridReady={onGridReady}
				onSelectionChanged={onSelectionChanged}
				onColumnStateChanged={onColumnStateChanged}
				dataqa="contacts-grid"
				showPagination
				onSortChanged={
					mode === 'localStorage' ? sortContactsFromLocalStorage : onSortChanged
				}
				fetchData={
					mode === 'localStorage'
						? fetchContactsByPageFromLocalStorage
						: fetchContactsByPage
				}
				loadingOverlayParams={{
					loadingMessage: 'Loading Contacts...',
				}}
				noRowsOverlayParams={{
					EmptyGridView: EmptyContactsGridView,
					activeMenuItemId,
				}}
			/>
		</div>
	);
};

ContactsGrid.propTypes = {
	// all contacts to be displayed in grid
	contacts: PropTypes.arrayOf(PropTypes.shape(Contact)),

	// server: come from fetchContacts API call
	// localStorage: used in selected section and when sending contact data to Mail app via "Print Postage" button
	mode: PropTypes.oneOf(['server', 'localStorage']),
};

export default ContactsGrid;
